/**
 * The state behind reading a real mail folder: which folder is open, the slice
 * of it that has been read, and which message is on screen.
 *
 * Every load here is a live IMAP session, which makes this unlike every other
 * list in the CRM:
 *
 * - Nothing loads on its own. A folder is read when it is opened, a message
 *   when it is picked, and neither is refetched behind the operator's back. In
 *   particular the first message is NOT auto-opened the way a thread folder
 *   opens its newest conversation - that would spend a second connection on
 *   something nobody asked to read.
 * - Answers can arrive out of order. Clicking three folders down a tree fires
 *   three reads against a mail server that answers in whatever order it likes,
 *   so every response carries a ticket and a stale one is dropped rather than
 *   painted over the folder the operator is actually looking at.
 * - The window grows, it does not page. "Show more" asks for a bigger newest-N
 *   rather than an offset: sequence numbers shift under deletes, and an offset
 *   into a mailbox somebody else is also using silently skips or repeats mail.
 *
 * Both pieces of state carry the folder they belong to and are DERIVED back to
 * empty when the folder changes, rather than cleared by an effect. An effect
 * would leave one render in which the new folder is paired with the old
 * folder's window - long enough to fire a second IMAP session for a window
 * nobody asked for, and to show the previous folder's message under the new
 * folder's name.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getImapMessage, getImapMessages, type ImapMessageBody, type ImapMessageRow } from "../../../../lib/mail";
import type { ImapTargets } from "../../../../lib/mailActions";
import { useMailboxEvents } from "./mailboxEvents";

/** One folder on one mailbox's mail server. */
export interface ImapSource {
  /** "shared" or an own mailbox id. */
  mailbox: string;
  path: string;
}

/**
 * The identity a folder is kept under here. The path alone is not enough once
 * the rail has several mailboxes: kristi@ and info@ both have an "INBOX.Sent".
 */
export function sourceKey(source: ImapSource): string {
  return `${source.mailbox}\n${source.path}`;
}

/** The first read, and the step each "show more" adds. Mirrors the API's own default. */
export const PAGE_STEP = 50;
/** The API clamps at this too - asking for more would silently answer less. */
export const MAX_MESSAGES = 200;

interface ListState {
  path: string | null;
  rows: ImapMessageRow[];
  total: number;
  /** From the folder's SELECT, and handed back on a read so a recreated folder
   *  cannot answer a UID that now belongs to a different message. */
  uidValidity: string;
  /** The folder's own special-use flag, and where one-click filing goes. */
  specialUse: string | null;
  targets: ImapTargets | null;
  busy: boolean;
  reason: string | null;
}

interface MessageState {
  path: string | null;
  uid: number | null;
  message: ImapMessageBody | null;
  busy: boolean;
  reason: string | null;
}

/** A cancelled request is not a failure - nobody was waiting for the answer. */
const isAbort = (err: unknown) => err instanceof DOMException && err.name === "AbortError";

const EMPTY_LIST: ListState = {
  path: null,
  rows: [],
  total: 0,
  uidValidity: "",
  specialUse: null,
  targets: null,
  busy: false,
  reason: null,
};
const EMPTY_MESSAGE: MessageState = { path: null, uid: null, message: null, busy: false, reason: null };

export interface ImapBrowse {
  rows: ImapMessageRow[];
  total: number;
  limit: number;
  listBusy: boolean;
  /** The server's own words for why the folder could not be read. */
  listReason: string | null;
  /** True while the folder holds more mail than has been read. */
  hasMore: boolean;
  uid: number | null;
  /** The listing's UIDVALIDITY, which a download has to quote back for the same
   *  reason a read does: a folder deleted and recreated must not answer a UID
   *  with somebody else's message. Empty when no folder has been read. */
  uidValidity: string;
  message: ImapMessageBody | null;
  msgBusy: boolean;
  msgReason: string | null;
  open: (uid: number) => void;
  /** Put the open message down without leaving the folder. A phone shows one
   *  pane at a time, so this is what its back button does. */
  close: () => void;
  showMore: () => void;
  reload: () => void;
  /** The open folder's special-use flag ("\\Trash"), null for an ordinary one. */
  specialUse: string | null;
  /** Where one-click Archive, Spam and Delete send a message, once the list has
   *  said. Null before that, and against an API older than the menu. */
  targets: ImapTargets | null;
  /**
   * Redraw one row after the menu changed it on the server. Takes the folder
   * the change was made in (its `sourceKey`), and does nothing if the pane has
   * since moved on to another one - a UID is only an address inside its own
   * folder.
   */
  patchRow: (key: string, uid: number, patch: Partial<ImapMessageRow>) => void;
  /** Take a row out after it was moved or deleted, closing it if it was open. */
  dropRow: (key: string, uid: number) => void;
}

/**
 * `path` below is the folder's `sourceKey`, not its path on the server: the
 * state was written for one mailbox and keeps its shape, with the mailbox
 * folded into the identity it compares. The requests take the real pair.
 */
export function useImapBrowse(source: ImapSource | null): ImapBrowse {
  const path = source ? sourceKey(source) : null;
  /** The real folder behind a key, for the requests. */
  const where = useRef(new Map<string, ImapSource>());
  if (source && path) where.current.set(path, source);
  const [win, setWin] = useState<{ path: string | null; limit: number }>({ path: null, limit: PAGE_STEP });
  const [list, setList] = useState<ListState>(EMPTY_LIST);
  const [picked, setPicked] = useState<MessageState>(EMPTY_MESSAGE);

  // One ticket counter per pane. A load may only write its result when nothing
  // newer has been asked for since.
  const listTicket = useRef(0);
  const msgTicket = useRef(0);
  // And a live request per pane, so a superseded one is CANCELLED rather than
  // merely ignored. The server runs these one at a time against a single mail
  // account: clicking down a tree of ten folders would otherwise put the one
  // folder the operator wants behind nine sessions nobody is waiting for.
  const listCall = useRef<AbortController | null>(null);
  const msgCall = useRef<AbortController | null>(null);
  /** What is open right now, for `dropRow`, which must not re-subscribe on every pick. */
  const pickedNow = useRef(picked);
  pickedNow.current = picked;

  const limit = win.path === path ? win.limit : PAGE_STEP;
  const shown = list.path === path ? list : EMPTY_LIST;
  const open = picked.path === path ? picked : EMPTY_MESSAGE;

  const runList = useCallback((folder: string, n: number) => {
    const ticket = (listTicket.current += 1);
    setList((prev) => ({ ...(prev.path === folder ? prev : EMPTY_LIST), path: folder, busy: true }));
    // Whatever was open belongs to the last time this folder was read. Keeping
    // it would repaint a body straight from memory under a pane that says it
    // reads live from the server - and the message may since have been moved,
    // deleted, or replaced by another one at the same UID.
    msgTicket.current += 1;
    msgCall.current?.abort();
    setPicked(EMPTY_MESSAGE);

    listCall.current?.abort();
    const call = new AbortController();
    listCall.current = call;

    const real = where.current.get(folder);
    if (!real) return;
    getImapMessages(real.path, n, call.signal, real.mailbox)
      .then((res) => {
        if (ticket !== listTicket.current) return;
        setList({
          path: folder,
          rows: res.ok ? res.rows : [],
          total: res.ok ? res.total : 0,
          uidValidity: res.ok ? res.uidValidity : "",
          specialUse: res.ok ? (res.specialUse ?? null) : null,
          targets: res.ok ? (res.targets ?? null) : null,
          busy: false,
          reason: res.ok ? null : (res.reason ?? "imap_error"),
        });
      })
      .catch((err) => {
        if (ticket !== listTicket.current || isAbort(err)) return;
        setList({ ...EMPTY_LIST, path: folder, reason: "imap_error" });
      });
  }, []);

  useEffect(() => {
    if (!path) {
      listTicket.current += 1;
      msgTicket.current += 1;
      listCall.current?.abort();
      msgCall.current?.abort();
      // Leaving the folder view clears what was open in it. Without this,
      // coming back to the same folder would show a body that was fetched at
      // some earlier point as if it had just been read off the server.
      setPicked(EMPTY_MESSAGE);
      setList(EMPTY_LIST);
      return;
    }
    runList(path, limit);
  }, [path, limit, runList]);

  const openMessage = useCallback(
    (uid: number) => {
      if (!path) return;
      const ticket = (msgTicket.current += 1);
      const folder = path;
      const validity = shown.uidValidity;
      msgCall.current?.abort();
      const call = new AbortController();
      msgCall.current = call;
      setPicked({ path: folder, uid, message: null, busy: true, reason: null });
      const real = where.current.get(folder);
      if (!real) return;
      getImapMessage(real.path, uid, validity, call.signal, real.mailbox)
        .then((res) => {
          if (ticket !== msgTicket.current) return;
          setPicked({
            path: folder,
            uid,
            message: res.ok && res.message ? res.message : null,
            busy: false,
            reason: res.ok && res.message ? null : (res.reason ?? "imap_error"),
          });
        })
        .catch((err) => {
          if (ticket !== msgTicket.current || isAbort(err)) return;
          setPicked({ path: folder, uid, message: null, busy: false, reason: "imap_error" });
        });
    },
    [path, shown.uidValidity],
  );

  // A read still in flight is cancelled as well as forgotten: the operator has
  // said they no longer want it, and the mail account runs one session at a
  // time.
  const closeMessage = useCallback(() => {
    msgTicket.current += 1;
    msgCall.current?.abort();
    setPicked(EMPTY_MESSAGE);
  }, []);

  const showMore = useCallback(() => {
    setWin({ path, limit: Math.min(MAX_MESSAGES, limit + PAGE_STEP) });
  }, [path, limit]);

  // Not routed through the effect: re-reading the same folder at the same size
  // changes none of its inputs, so it would never fire.
  const reload = useCallback(() => {
    if (path) runList(path, limit);
  }, [path, limit, runList]);

  // The rail marked a whole folder read. When that is the folder on screen,
  // every row's bold weight is now wrong, and only a fresh read knows which.
  useMailboxEvents((event) => {
    if (event.kind === "folderRead" && source && event.mailbox === source.mailbox && event.path === source.path) reload();
  });

  const patchRow = useCallback((folder: string, uid: number, patch: Partial<ImapMessageRow>) => {
    setList((prev) =>
      prev.path === folder ? { ...prev, rows: prev.rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)) } : prev,
    );
  }, []);

  const dropRow = useCallback((folder: string, uid: number) => {
    setList((prev) => {
      if (prev.path !== folder || !prev.rows.some((r) => r.uid === uid)) return prev;
      return { ...prev, rows: prev.rows.filter((r) => r.uid !== uid), total: Math.max(0, prev.total - 1) };
    });
    // The pane must not go on showing a message the server no longer has here.
    const open = pickedNow.current;
    if (open.path === folder && open.uid === uid) {
      msgTicket.current += 1;
      msgCall.current?.abort();
      setPicked(EMPTY_MESSAGE);
    }
  }, []);

  return {
    rows: shown.rows,
    total: shown.total,
    limit,
    listBusy: shown.busy,
    listReason: shown.reason,
    hasMore: shown.rows.length < shown.total && limit < MAX_MESSAGES,
    uid: open.uid,
    uidValidity: shown.uidValidity,
    message: open.message,
    msgBusy: open.busy,
    msgReason: open.reason,
    open: openMessage,
    close: closeMessage,
    showMore,
    reload,
    specialUse: shown.specialUse,
    targets: shown.targets,
    patchRow,
    dropRow,
  };
}
